import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, of, catchError } from 'rxjs';
import { NewsletterPost, SubstackService } from '../../../core/services/substack.service';

@Component({
  selector: 'app-newsletter',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './newsletter.html',
  styleUrl: './newsletter.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Newsletter implements OnInit, OnDestroy {
  private scrollContainer: ElementRef<HTMLElement> | undefined;

  // ✅ Setter en vez de @ViewChild fijo: se dispara automáticamente cada vez
  // que el elemento aparece o desaparece del DOM (por ejemplo, cuando el
  // bloque @if lo renderiza recién al recibir los posts). Así el autoplay
  // arranca en el momento correcto, sin importar cuánto tarde el HTTP.
  @ViewChild('scrollContainer')
  set scrollContainerRef(el: ElementRef<HTMLElement> | undefined) {
    this.scrollContainer = el;
    if (el) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
  }

  posts$: Observable<NewsletterPost[]> = of([]);
  loading = true;
  error = false;

  private autoplayTimer: ReturnType<typeof setInterval> | null = null;
  private readonly AUTOPLAY_INTERVAL_MS = 4000;
  private isPaused = false;

  constructor(private substackService: SubstackService) { }

  ngOnInit(): void {
    this.loadPosts();
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
  }

  loadPosts(): void {
    this.loading = true;
    this.error = false;

    this.posts$ = this.substackService.getPosts().pipe(
      catchError(() => {
        this.error = true;
        this.loading = false;
        return of([]);
      })
    );

    setTimeout(() => {
      this.loading = false;
      // El autoplay ya NO se arranca acá: lo dispara el setter de
      // scrollContainerRef en cuanto el elemento exista realmente en el DOM.
    }, 0);
  }

  retryLoadPosts(): void {
    this.loadPosts();
  }

  scroll(direction: 'left' | 'right'): void {
    this.scrollByDirection(direction);
    // Si el usuario interactúa manualmente, reiniciamos el conteo del autoplay
    this.restartAutoplay();
  }

  // ===== AUTOPLAY =====

  private startAutoplay(): void {
    this.stopAutoplay(); // por si ya había uno corriendo

    this.autoplayTimer = setInterval(() => {
      if (this.isPaused) return;
      this.autoplayTick();
    }, this.AUTOPLAY_INTERVAL_MS);
  }

  private stopAutoplay(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  private restartAutoplay(): void {
    this.startAutoplay();
  }

  private autoplayTick(): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const tolerance = 10;

    if (container.scrollLeft >= maxScroll - tolerance) {
      // Llegamos al final -> volvemos al principio
      container.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.scrollByDirection('right');
    }
  }

  private scrollByDirection(direction: 'left' | 'right'): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container) return;

    const scrollAmount = container.offsetWidth * 0.8;

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }

  // ===== PAUSA EN HOVER / TOUCH =====

  onPointerEnter(): void {
    this.isPaused = true;
  }

  onPointerLeave(): void {
    this.isPaused = false;
  }
}